import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
  Container,
  Fab
} from '@mui/material';
import {
  ArrowBack,
  SentimentVeryDissatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVerySatisfied
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { experienciaService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface ExperienciaData {
  id: number;
  id_usuario: number;
  usuario_nome?: string;
  cliente_nome?: string;
  id_cliente: number;
  criado_em: string;
  meu_humor: string;
  experiencia_positiva: string;
  experiencia_negativa: string;
}

const ExperienciaFicha: React.FC = () => {
  console.log(' [DEBUG] ExperienciaFicha - Componente renderizado!');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  console.log(' [DEBUG] ExperienciaFicha - ID da URL:', id);
  console.log(' [DEBUG] ExperienciaFicha - Usuário:', user);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [experiencia, setExperiencia] = useState<ExperienciaData | null>(null);

  const humorOptions = [
    { value: 1, text: 'muito_triste', icon: SentimentVeryDissatisfied, label: 'Muito Triste', color: '#f44336' },
    { value: 2, text: 'triste', icon: SentimentDissatisfied, label: 'Triste', color: '#ff9800' },
    { value: 3, text: 'neutro', icon: SentimentNeutral, label: 'Neutro', color: '#9e9e9e' },
    { value: 4, text: 'feliz', icon: SentimentSatisfied, label: 'Feliz', color: '#4caf50' },
    { value: 5, text: 'muito_feliz', icon: SentimentVerySatisfied, label: 'Muito Feliz', color: '#2196f3' }
  ];

  const convertHumorToNumber = (humorText: string): number => {
    const humorMap: { [key: string]: number } = {
      'muito_triste': 1, 'triste': 2, 'neutro': 3, 'feliz': 4, 'muito_feliz': 5
    };
    return humorMap[humorText] || 3;
  };

  const formatarData = (data: string): string => {
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const formatarDataHora = (data: string): string => {
    return new Date(data).toLocaleString('pt-BR');
  };

  useEffect(() => {
    const carregarExperiencia = async () => {
      console.log(' [DEBUG] ExperienciaFicha - ID recebido:', id);
      
      if (!id) {
        console.log(' [ERROR] ExperienciaFicha - ID não fornecido');
        setError('ID da experiência não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log(' [DEBUG] ExperienciaFicha - Chamando API para ID:', parseInt(id));
        
        const response = await experienciaService.buscarPorId(parseInt(id));
        
        console.log(' [DEBUG] ExperienciaFicha - Response completa:', response);
        console.log(' [DEBUG] ExperienciaFicha - Response.data:', response.data);
        console.log(' [DEBUG] ExperienciaFicha - Tipo de response.data:', typeof response.data);
        console.log(' [DEBUG] ExperienciaFicha - Chaves de response.data:', Object.keys(response.data || {}));
        console.log(' [DEBUG] ExperienciaFicha - JSON.stringify response.data:', JSON.stringify(response.data, null, 2));
        
        let dadosExperiencia = null;
        
        if (response.data && response.data.data) {
          // Formato com wrapper: { success: true, data: {...} }
          dadosExperiencia = response.data.data;
          console.log(' [DEBUG] ExperienciaFicha - Usando dados do wrapper response.data.data');
        } else if (response.data && response.data.id) {
          // Formato direto: { id, meu_humor, ... }
          dadosExperiencia = response.data;
          console.log(' [DEBUG] ExperienciaFicha - Usando dados diretos response.data');
        }
        
        if (dadosExperiencia) {
          console.log(' [DEBUG] ExperienciaFicha - Dados mapeados:', dadosExperiencia);
          console.log(' [DEBUG] ExperienciaFicha - Campo cliente_nome:', dadosExperiencia.cliente_nome);
          console.log(' [DEBUG] ExperienciaFicha - Campo criado_em:', dadosExperiencia.criado_em);
          console.log(' [DEBUG] ExperienciaFicha - Campo meu_humor:', dadosExperiencia.meu_humor);
          console.log(' [DEBUG] ExperienciaFicha - Campo experiencia_positiva:', dadosExperiencia.experiencia_positiva);
          console.log(' [DEBUG] ExperienciaFicha - Campo experiencia_negativa:', dadosExperiencia.experiencia_negativa);
          
          setExperiencia(dadosExperiencia);
        } else {
          console.log(' [ERROR] ExperienciaFicha - Dados não encontrados na resposta');
          setError('Experiência não encontrada');
        }
      } catch (err) {
        console.error(' [ERROR] ExperienciaFicha - Erro ao carregar experiência:', err);
        // Corrigir tipagem TypeScript - fazer type assertion para AxiosError
        const error = err as any;
        if (error.response?.data?.message) {
          setError(error.response.data.message);
        } else {
          setError('Erro ao carregar experiência');
        }
      } finally {
        setLoading(false);
      }
    };

    carregarExperiencia();
  }, [id]);

  const handleVoltar = () => {
    navigate('/app/experiencias');
  };

  const getHumorDisplay = () => {
    if (!experiencia?.meu_humor) return null;
    
    const humorValue = convertHumorToNumber(experiencia.meu_humor);
    const humor = humorOptions.find(h => h.value === humorValue);
    
    if (!humor) return null;
    
    const IconComponent = humor.icon;
    
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        gap={1}
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: `${humor.color}20`,
          border: `2px solid ${humor.color}`
        }}
      >
        <IconComponent
          sx={{
            fontSize: 40,
            color: humor.color
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: humor.color,
            fontWeight: 'bold'
          }}
        >
          {humor.label}
        </Typography>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={handleVoltar}
          startIcon={<ArrowBack />}
        >
          Voltar
        </Button>
      </Container>
    );
  }

  if (!experiencia) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Experiência não encontrada
        </Alert>
        <Button
          variant="contained"
          onClick={handleVoltar}
          startIcon={<ArrowBack />}
        >
          Voltar
        </Button>
      </Container>
    );
  }

  // Calcular a data formatada antes do JSX
  const dataFormatada = experiencia?.criado_em ? formatarDataHora(experiencia.criado_em) : 'N/A';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: { xs: 1, md: 2 },
        px: { xs: 1, md: 0 },
        position: 'relative'
      }}
    >
      <Fab
        color="primary"
        onClick={handleVoltar}
        sx={{
          position: 'fixed',
          top: { xs: 60, md: 80 },
          right: { xs: 10, md: 20 },
          zIndex: 1000,
          boxShadow: 3,
          width: { xs: 48, md: 56 },
          height: { xs: 48, md: 56 }
        }}
      >
        <ArrowBack />
      </Fab>

      <Container maxWidth="md" sx={{ px: { xs: 1, md: 3 } }}>
        <Card
          elevation={8}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            mt: { xs: 1, md: 0 }
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
            <Box textAlign="center" mb={{ xs: 1.5, md: 2 }}>
              <Typography
                variant={isMobile ? 'h6' : 'h4'}
                component="h1"
                fontWeight="bold"
                color="primary"
                gutterBottom
              >
                Visualizar Experiência
              </Typography>
              <Box>
                <Typography variant={isMobile ? 'body2' : 'body1'} color="text.secondary">
                  Cliente: {experiencia?.cliente_nome || 'N/A'}
                </Typography>
                <Typography variant={isMobile ? 'caption' : 'body2'} color="text.secondary" mt={0.5}>
                  Data: {dataFormatada}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: { xs: 1.5, md: 2 } }} />

            <Grid container spacing={{ xs: 1.5, md: 2 }}>
              <Grid item xs={12}>
                <Paper elevation={1} sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Typography 
                    variant={isMobile ? 'subtitle1' : 'h6'} 
                    gutterBottom 
                    color="primary" 
                    textAlign="center"
                  >
                    Humor do Dia
                  </Typography>
                  <Box display="flex" justifyContent="center" mt={1}>
                    {getHumorDisplay()}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: { xs: 1.5, md: 2 }, height: '100%' }}>
                  <Typography 
                    variant={isMobile ? 'subtitle1' : 'h6'} 
                    gutterBottom 
                    color="primary"
                  >
                    Experiência Positiva do Dia
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 3 : 4}
                    value={experiencia?.experiencia_positiva || 'Nenhuma experiência positiva registrada.'}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        fontSize: isMobile ? '0.875rem' : '1rem'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        '& fieldset': {
                          borderColor: '#4caf50'
                        }
                      }
                    }}
                  />
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={1} sx={{ p: { xs: 1.5, md: 2 }, height: '100%' }}>
                  <Typography 
                    variant={isMobile ? 'subtitle1' : 'h6'} 
                    gutterBottom 
                    color="primary"
                  >
                    Experiência Negativa do Dia
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={isMobile ? 3 : 4}
                    value={experiencia?.experiencia_negativa || 'Nenhuma experiência negativa registrada.'}
                    variant="outlined"
                    InputProps={{
                      readOnly: true,
                      sx: {
                        fontSize: isMobile ? '0.875rem' : '1rem'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#f8f9fa',
                        '& fieldset': {
                          borderColor: '#f44336'
                        }
                      }
                    }}
                  />
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ExperienciaFicha;